import type { ComputedRef } from 'vue'
import { computed, toRefs, unref } from 'vue'
import usePlayer from '@/composables/usePlayer'
import { getLastEndedCampaign } from '@/helpers/getLastEndedCampaign'

/**
 * Derived booleans for “hibernation clock” idle (queue empty, tenant registered).
 * Shared by {@link PlayerIdleLayer} phase routing and {@link PlayerIdleHibernationView}.
 */
export function usePlayerIdleHibernationSignals(): {
  showWaitingForNextCampaign: ComputedRef<boolean>
  showActiveCampaignNoContent: ComputedRef<boolean>
  showHibernationScreen: ComputedRef<boolean>
  lastEndedCampaign: ComputedRef<ReturnType<typeof getLastEndedCampaign>>
  showLastCampaignEndedScreen: ComputedRef<boolean>
} {
  const { state } = usePlayer()
  const { tenant, queue, campaign, nextCampaign, registrationScheduleRoots } = toRefs(state)

  const showWaitingForNextCampaign = computed(() => {
    return (
      unref(tenant) !== null
      && unref(queue).length < 1
      && unref(campaign) === null
      && unref(nextCampaign) !== null
    )
  })

  // Do not gate on loadingCampaign because registration soft-reloads flip LOADING briefly while queue stays empty,
  // bouncing idle phase hibernation↔waiting and flashing black via out-in transitions. Download chrome still wins
  // in PlayerIdleLayer when delayed progress shows.
  const showActiveCampaignNoContent = computed(() => {
    return (
      unref(tenant) !== null
      && unref(queue).length < 1
      && unref(campaign) !== null
    )
  })

  const showHibernationScreen = computed(
    () => unref(showWaitingForNextCampaign) || unref(showActiveCampaignNoContent)
  )

  const lastEndedCampaign = computed(() => {
    const roots = unref(registrationScheduleRoots)
    return getLastEndedCampaign(roots as import('@/helpers/getLastEndedCampaign').CampaignWithEndDate[])
  })

  const showLastCampaignEndedScreen = computed(() => {
    return (
      unref(tenant) !== null
      && unref(queue).length < 1
      && unref(campaign) === null
      && unref(nextCampaign) === null
      && unref(lastEndedCampaign) !== null
    )
  })

  return {
    showWaitingForNextCampaign,
    showActiveCampaignNoContent,
    showHibernationScreen,
    lastEndedCampaign,
    showLastCampaignEndedScreen
  }
}
